/**
 * Utilitaire central pour gérer les URLs d'API selon l'environnement
 * Ce fichier est la source unique de vérité pour les URLs d'API dans l'application
 */

// Types
export interface ApiConfig {
  baseUrl: string;
  apiPath: string;
  fullApiUrl: string;
  isProduction: boolean;
  isDevelopment: boolean;
}

// Détection de l'environnement
const isDevelopment = import.meta.env?.MODE === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

const isProduction = import.meta.env?.MODE === 'production' || 
                    (!isDevelopment && window.location.hostname !== 'localhost');

// Configuration des URLs d'API par environnement
const getApiConfig = (): ApiConfig => {
  // Variables d'environnement - vérifier plusieurs variables possibles
  const envApiUrl = import.meta.env?.VITE_API_URL || import.meta.env?.VITE_BACKEND_URL;
  
  // Logs uniquement en développement et seulement la première fois
  const isDebugEnabled = isDevelopment && !window.__apiConfigInitialized;
  if (isDebugEnabled) {
    console.log("Environment API URL:", envApiUrl);
    console.log("Environment Mode:", import.meta.env?.MODE);
    console.log("Hostname:", window.location.hostname);
    
    // Ne pas logger les variables d'environnement sensibles
    const safeEnvVars = { ...import.meta.env };
    // Masquer les clés API et autres informations sensibles
    if (safeEnvVars.VITE_GOOGLE_MAPS_API_KEY) safeEnvVars.VITE_GOOGLE_MAPS_API_KEY = '[MASKED]';
    if (safeEnvVars.VITE_FIREBASE_API_KEY) safeEnvVars.VITE_FIREBASE_API_KEY = '[MASKED]';
    // Masquer d'autres clés sensibles potentielles
    Object.keys(safeEnvVars).forEach(key => {
      if (key.includes('KEY') || key.includes('SECRET') || key.includes('PASSWORD') || key.includes('TOKEN')) {
        safeEnvVars[key] = '[MASKED]';
      }
    });
    console.log("Environment variables:", safeEnvVars);
  }
  
  // Configuration par défaut
  let baseUrl = '';
  let apiPath = '/api';
  
  if (isDevelopment) {
    // En développement local
    // Priorité 1: Variable d'environnement
    if (envApiUrl) {
      // Si l'URL complète est fournie
      if (envApiUrl.includes('://')) {
        baseUrl = envApiUrl.endsWith('/api') 
          ? envApiUrl.substring(0, envApiUrl.length - 4) 
          : envApiUrl;
      } else {
        // Si c'est juste le chemin relatif
        baseUrl = 'http://localhost:3001';
      }
    } else {
      // Valeur par défaut pour le développement
      baseUrl = 'http://localhost:3001';
    }
  } else if (isProduction) {
    // En production
    if (envApiUrl) {
      // Si une URL d'API spécifique est fournie pour la production
      console.log("Using environment API URL in production:", envApiUrl);
      if (envApiUrl.includes('://')) {
        baseUrl = envApiUrl.endsWith('/api') 
          ? envApiUrl.substring(0, envApiUrl.length - 4) 
          : envApiUrl;
      } else {
        // URL relative en production, utiliser le domaine actuel
        baseUrl = window.location.origin;
      }
    } else {
      // Cas spécial pour Vercel + Render: si nous sommes sur vercel.app, utiliser Render
      if (window.location.hostname.includes('vercel.app')) {
        console.log("Detected Vercel deployment, using Render backend");
        baseUrl = 'https://etudown.onrender.com';
      } 
      // Cas spécial pour le domaine etudown.com: utiliser le backend sur Render
      else if (window.location.hostname.includes('etudown.com')) {
        console.log("Detected etudown.com domain, using Render backend");
        baseUrl = 'https://etudown.onrender.com';
      }
      else {
        // Par défaut, utiliser le même domaine en production
        baseUrl = window.location.origin;
      }
    }
  } else {
    // Pour les environnements de test, staging, etc.
    baseUrl = envApiUrl || window.location.origin;
  }
  
  // Construire l'URL complète de l'API
  const fullApiUrl = baseUrl + apiPath;
  
  if (isDebugEnabled) {
    console.log("API Config:", { baseUrl, apiPath, fullApiUrl, isProduction, isDevelopment });
  }
  
  return {
    baseUrl,
    apiPath,
    fullApiUrl,
    isProduction,
    isDevelopment
  };
};

// Exporte l'instance de configuration unique
export const API_CONFIG = getApiConfig();

// Export de l'URL de base pour rétrocompatibilité
export const API_BASE_URL = API_CONFIG.baseUrl;

// Fonction utilitaire pour construire des URL d'API
export const buildApiUrl = (path: string): string => {
  // Si l'URL est déjà absolue, la retourner telle quelle
  if (path.startsWith('http')) {
    return path;
  }
  
  // Si le chemin commence déjà par /api, l'utiliser tel quel
  if (path.startsWith(API_CONFIG.apiPath)) {
    return `${API_CONFIG.baseUrl}${path}`;
  }
  
  // Sinon, ajouter le préfixe /api
  return `${API_CONFIG.fullApiUrl}${path.startsWith('/') ? path : `/${path}`}`;
};
